/**
 * App-side wiring for the Heidi II surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move. That matters more here than it usually does: `Heidi2AwarenessBanner`
 * renders on every page and the nudges render conditionally, so resolving a
 * value at a mount would move a PostHog exposure or a query to a render that
 * does not happen today.
 *
 * TWO INPUTS ARE DELIBERATELY ABSENT, and both are weight rather than taste.
 * `useHeidi2AccessControl` is bound inside the lazily-loaded confirm-access
 * mount (`global-modals-cloud-only-heidi-2-confirm-access.tsx`), which is where
 * it already was; and the "Get started" checklist's progress inputs cross as a
 * `ports` prop from `agent-home-panel.tsx`, its only mount. Binding either here
 * would pull its module into `_app`'s eager cone for every page load.
 *
 * It imports `@heidi/agents/data` and never the `.` UI door: this module is
 * evaluated at `_app` module scope, and a static `.` import here would put that
 * door in the eager graph.
 *
 * Imported for effect at module scope in `_app.tsx`.
 */
import {
  useIsHeidi2OnboardingEnabled,
  useIsHeidi2OptInEnabled,
} from '@/app-shell/flags/agents-flags';
import { useHeidi2WelcomeCtaCopy } from '@/app-shell/flags/heidi-2-welcome-cta-flag';
import { useChatSidebarHandlers } from '@/app-shell/layout/Navigation/hooks/useChatSidebarHandlers';
import { useAgentHomePanel } from '@/app-shell/layout/Navigation/nav-v3/agent-home-panel-state';
import { useBannerIdentity } from '@/app-shell/layout/use-banner-identity';
import { isHomeBootPath } from '@/atoms/bootLandingAtoms';
import { appStateForModalsAtom } from '@/atoms/launchModalsConfigAtom';
import {
  admittedModalQueueAtom,
  currentQueuedModalAtom,
  seenModalIdsAtom,
} from '@/atoms/modalQueueAtoms';
import {
  TopStripPriority,
  useClaimTopStrip,
  useReportHeidi2Strip,
  useReportTopStrip,
} from '@/atoms/top-strip-atoms';
import { getEhrLogoUrl } from '@/helpers/getEhrLogoUrl';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useQueryRoles } from '@/hooks/query/useQueryRoles';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { useUserCanonicalSpecialtyName } from '@/hooks/useUserCanonicalSpecialtyName';
import { isModalClosed } from '@/lib/modalClosedRules';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import {
  configureHeidi2Ports,
  pendingWorkHomeViewAtom,
  useBaseChatHref,
  type Heidi2Ports,
} from '@heidi/agents/data';
import {
  isMaxBillingSeat,
  useQueryCreditAllowance,
  useWorkspaceIdSettledForCreditUsagePopoverLaunch,
} from '@heidi/commerce';
import {
  isComplimentaryCreditAllowance,
  useMutateActivateHeidi2,
  useMutateRequestHeidi2Access,
  useUserBillingInfo,
} from '@heidi/commerce/data';
import { cameFromDictateDownloadEntryAtom } from '@heidi/dictate/data';
import { NANGO_SETTINGS_PATH } from '@heidi/integrations/connected-apps/contracts';
import { GET_USER } from '@heidi/workspace/contracts';
import {
  useMutateMarkModalAsViewed,
  useQueryTeamAccessEntitlements,
  useQueryUser,
} from '@heidi/workspace/data';
import {
  hashKey,
  notifyManager,
  useQueryClient,
  type QueryState,
} from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useSyncExternalStore } from 'react';

/** The queue head's id, as a named hook so the rules of hooks apply through it. */
const useCurrentQueuedModalId = () => useAtomValue(currentQueuedModalAtom)?.id;

/**
 * The queue head before tier and pacing gates — the modal that would show
 * next, not the one showing. Skips popups the native e2e lane withholds.
 */
const useQueueHeadModalId = () => useAtomValue(admittedModalQueueAtom).at(0)?.id;

/** The three launch-state fields the Heidi II welcome cohort reads. */
const useModalLaunchState = () => {
  const { isUserDataLoaded, isUserPending, isKindePropertiesPending } =
    useAtomValue(appStateForModalsAtom);
  return { isUserDataLoaded, isUserPending, isKindePropertiesPending };
};

/** Stages the Work-home view so the tour's finish lands on Home. */
const useSetPendingWorkHomeView = () => useSetAtom(pendingWorkHomeViewAtom);

/** The connected EHR's logo, or `undefined` for the generic tile. */
const useEhrLogoSrc = () => getEhrLogoUrl(useIntegrationEhrInfo().integrationEhrProvider);

/** The backend-synced seen set, reactively — the Home dot re-renders on hydration. */
const useSeenModalIds = () => useAtomValue(seenModalIdsAtom);

/** `router.pathname`. The route itself stays app-owned; only the string crosses. */
const usePathname = () => useRouter().pathname;

/** `router.asPath` — where the tour puts someone back when they decline it. */
const useRouterAsPath = () => useRouter().asPath;

/** The Home panel opener, the same one the Home nav item uses. */
const useOpenHomePanel = () => useAgentHomePanel().open;

/** `router.push`, so the checklist's skills item keeps its shallow navigation. */
const useNavigate = () => {
  const router = useRouter();
  return (href: string, options?: { shallow?: boolean }) =>
    void router.push(href, undefined, options);
};

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMarkModalAsViewed = () => useMutateMarkModalAsViewed().mutate;

/** A visit that started at the Dictate download entry skips the welcome takeover. */
const useIsHeidi2WelcomeSuppressed = () => useAtomValue(cameFromDictateDownloadEntryAtom);

const useClaimHeidi2TopStrip = (
  id: 'heidi-2-awareness' | 'heidi-2-request-access' | 'heidi-2-explore-later',
  isEligible: boolean
) => useClaimTopStrip(id, TopStripPriority.HEIDI_2_FAMILY, isEligible);

const APP_STARTED_AT = Date.now();

const GET_USER_QUERY_HASH = hashKey([GET_USER]);

// Not useIsFetching: it scans every cached query on each cache event.
const useUserQueryState = () => {
  const queryCache = useQueryClient().getQueryCache();
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const notify = notifyManager.batchCalls(onStoreChange);
      return queryCache.subscribe((event) => {
        if (event.query.queryHash === GET_USER_QUERY_HASH) notify();
      });
    },
    [queryCache]
  );
  const getSnapshot = () =>
    queryCache.get(GET_USER_QUERY_HASH)?.state as
      | QueryState<{ user_ref_id: string }>
      | undefined;
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

const useStripReadiness = () => {
  const flagsReady = usePosthogFlagsReady();
  const userId = useBannerIdentity();
  // Subscribe to fetch completion even when structural sharing keeps user data
  // unchanged. Restored seen_modals must not release the strip before refetch.
  const userQuery = useUserQueryState();
  return {
    userId,
    isReady:
      flagsReady &&
      !!userId &&
      userQuery?.data?.user_ref_id === userId &&
      userQuery.dataUpdatedAt >= APP_STARTED_AT,
  };
};

export const heidi2Ports: Heidi2Ports = {
  useIsHeidi2OnboardingEnabled,
  useUserBillingInfo,
  useQueryUser,
  useSeenModalIds,
  isModalClosed,
  useQueuedModal,
  useCurrentQueuedModalId,
  useQueueHeadModalId,
  useModalLaunchState,
  useMarkModalAsViewed,
  useMutateRequestHeidi2Access,
  usePathname,
  useOpenHomePanel,
  useNavigate,
  useChatSidebarHandlers,
  connectAppsHref: NANGO_SETTINGS_PATH,
  useRouterAsPath,
  useBaseChatHref,
  isHomeBootPath,
  useSetPendingWorkHomeView,
  useReportTopStrip,
  useClaimTopStrip: useClaimHeidi2TopStrip,
  useStripReadiness,
  useReportHeidi2Strip,
  useIsHeidi2OptInEnabled,
  useHeidi2WelcomeCtaCopy,
  useWorkspaceIdSettledForCreditUsagePopoverLaunch,
  useIsHeidi2WelcomeSuppressed,
  useQueryRoles,
  useQueryTeamAccessEntitlements,
  isMaxBillingSeat,
  useUserCanonicalSpecialtyName,
  useEhrLogoSrc,
  useMutateActivateHeidi2,
  useIsClinicalRecordingActive,
  useCreditAllowance: useQueryCreditAllowance,
  isComplimentaryCreditAllowance,
  isAppleCompliantBuild,
  isOnPrem,
};

configureHeidi2Ports(heidi2Ports);
